import { resolveSubscriptionAccess } from './subscriptionHelper';

// Real Estate accounts get their own dedicated routes (see
// realEstate.routes.js) and the old Appointments / Organization / Branches
// nav entries are hidden for them via AVAILABLE_MODULES's
// `disabledMenuItems` (routes/dashboard/settings/module/constants/modules.js)
// — but that only affects the sidebar, not direct URL navigation. This
// wraps an existing route guard so real_estate accounts are redirected to
// the dashboard before the wrapped guard runs, mirroring the
// `moduleAccessGuard` pattern already used by realEstate.routes.js:39-42.
//
// `store` is taken as a parameter rather than imported, same as
// subscriptionHelper.js's own guards — helper files in this codebase don't
// import the store directly.
//
// The subscription is resolved here only to read `stripe/getModuleName`
// (which requires the subscription/module fetch to have completed).
// Deliberately not treated as the guard's own subscription check: on error
// or "no active subscription" this falls through to the wrapped guard,
// which performs its own (cached) subscription resolution and owns
// redirecting to `subscription_error` / `pricing_table`.
// The active module is only loaded by App.vue after the account boots, so
// on a cold load (refresh or pasted URL) a route guard can run before it
// exists. Fetch it here when missing, same idea as condoMngt.routes.js's
// ensureCondoModule. A failed fetch leaves it unknown (null), which callers
// treat as "not this module".
export async function resolveModuleName(store, accountId) {
  if (!store.getters['stripe/getModuleName']) {
    try {
      await store.dispatch('stripe/fetchModule', accountId);
    } catch (e) {
      // handled by the caller: unknown module
    }
  }
  return store.getters['stripe/getModuleName'];
}

export function withRealEstateRedirect(guard, store) {
  return async (to, from, next) => {
    if (process.env.CYPRESS_TEST_BUILD === 'true' && window.Cypress) {
      return guard(to, from, next);
    }

    const accountId = to.params.accountId;
    const { hasActiveSubscription, hasError } = await resolveSubscriptionAccess(
      store,
      accountId
    );

    if (
      !hasError &&
      hasActiveSubscription &&
      (await resolveModuleName(store, accountId)) === 'real_estate'
    ) {
      return next({ name: 'dashboard', params: { accountId } });
    }

    return guard(to, from, next);
  };
}
